/**
 * Customer cloud accounts attached to a tenant.
 *
 * A tenant is a customer; a cloud account is one place we may deploy for them.
 * The two are separate because a customer routinely has more than one — a trial
 * account, then production, sometimes one per region or business unit.
 */
/**
 * Clouds this can register an account in.
 *
 * Azure joined at IM-44, and did so the way this was written to allow: every
 * place that branches on the cloud stopped compiling until it handled the new
 * member. The prototype had the opposite: a `platform` string threaded
 * everywhere, switches that fell through to `require`-ing modules that did not
 * exist, and — the bug this criterion names — **AWS role ARNs synthesised
 * regardless of which platform was asked for.** A string cannot fail to compile;
 * this can.
 */
export const CLOUDS = ['aws', 'azure'];
export const isCloud = (value) => CLOUDS.includes(value);
/** Exactly twelve digits. Anything else can never be assumed into. */
const AWS_ACCOUNT_ID = /^\d{12}$/;
/**
 * A GUID as Azure prints one: 8-4-4-4-12 hex, either case.
 *
 * Subscription and tenant ids are both of this shape, and nothing narrower is
 * documented — Azure does not promise a version nibble — so this checks the
 * shape and nothing more.
 */
const GUID = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
export const isGuid = (value) => GUID.test(value);
/**
 * What an Azure account delegates to Qrvey, chosen at registration (IM-44,
 * decided by Amit on 2026-11-02).
 *
 * - `subscription`: the whole subscription, **only** when it is dedicated to
 *   Qrvey.
 * - `resource-groups`: one delegation per instance resource group, for a
 *   subscription the customer uses for anything else.
 *
 * A choice the customer makes rather than a default this side picks, because
 * the Reader role Lighthouse delegates can query every Log Analytics workspace
 * in its scope. At subscription scope in a shared subscription that is every
 * log the customer's other applications write — the IM-51 "log reads on `*`"
 * gap, with no deny on Azure that could close it.
 */
export const DELEGATION_SCOPES = ['subscription', 'resource-groups'];
export const isDelegationScope = (value) => typeof value === 'string' && DELEGATION_SCOPES.includes(value);
export class InvalidCloudAccountError extends Error {
}
/**
 * The identifier a cloud account is stored and addressed by.
 *
 * Cloud-qualified, so an AWS account and an Azure subscription could never
 * collide in one tenant's partition. An Azure key is lower-cased: Azure treats
 * a GUID case-insensitively and prints it either way, and two keys for one
 * subscription would register it twice.
 */
export const cloudAccountKey = (account) => {
    switch (account.cloud) {
        case 'aws':
            return `aws:${account.awsAccountId}`;
        case 'azure':
            return `azure:${account.subscriptionId.toLowerCase()}`;
    }
};
/**
 * The cloud an account key names.
 *
 * Keys are cloud-qualified by construction (`aws:123456789012`), so the cloud is
 * readable from the key without a store lookup — which is what lets an endpoint
 * holding only a key pick an adapter. Returns undefined for anything that is not
 * a key this system issued, and callers treat that as "no such account" rather
 * than as a malformed request: whether a key is well-formed is not something to
 * confirm to a caller who cannot see the account.
 */
export const cloudFromAccountKey = (accountKey) => {
    const separator = accountKey.indexOf(':');
    if (separator <= 0)
        return undefined;
    const cloud = accountKey.slice(0, separator);
    return isCloud(cloud) ? cloud : undefined;
};
/**
 * The AWS account id a key names, or undefined if it does not name one.
 *
 * Undefined covers both "this is another cloud's key" and "this is not a key we
 * issued", because a caller that wants the twelve digits can do nothing useful
 * with either. Validated rather than sliced: a key is data that has been through
 * storage, and a caller building an ARN or a bucket name out of a malformed one
 * would address something it did not mean to.
 */
export const awsAccountIdFromAccountKey = (accountKey) => {
    if (cloudFromAccountKey(accountKey) !== 'aws')
        return undefined;
    const awsAccountId = accountKey.slice('aws:'.length);
    return AWS_ACCOUNT_ID.test(awsAccountId) ? awsAccountId : undefined;
};
/** The subscription id a key names, or undefined. Validated, for the reason above. */
export const subscriptionIdFromAccountKey = (accountKey) => {
    if (cloudFromAccountKey(accountKey) !== 'azure')
        return undefined;
    const subscriptionId = accountKey.slice('azure:'.length);
    return GUID.test(subscriptionId) ? subscriptionId : undefined;
};
/**
 * Validates registration input.
 *
 * Rejecting a malformed identifier here means the failure is a 400 at
 * registration rather than an `AccessDenied` from `sts:AssumeRole`, or a 404
 * from Azure Resource Manager, days later, when nobody remembers typing it.
 *
 * Each branch builds its result from named fields rather than spreading the
 * input, so a field another cloud carries — or a minted value somebody tried
 * to supply — cannot ride through.
 */
export const validateRegistration = (input) => {
    if (typeof input.cloud !== 'string' || !isCloud(input.cloud)) {
        throw new InvalidCloudAccountError(`cloud must be one of: ${CLOUDS.join(', ')}`);
    }
    const label = typeof input.label === 'string' ? input.label.trim() : '';
    if (label === '' || label.length > 100) {
        throw new InvalidCloudAccountError('label must be 1 to 100 characters');
    }
    switch (input.cloud) {
        case 'aws': {
            const awsAccountId = typeof input.awsAccountId === 'string' ? input.awsAccountId.trim() : '';
            if (!AWS_ACCOUNT_ID.test(awsAccountId)) {
                throw new InvalidCloudAccountError('awsAccountId must be exactly 12 digits');
            }
            return { cloud: 'aws', awsAccountId, label };
        }
        case 'azure': {
            const subscriptionId = typeof input.subscriptionId === 'string' ? input.subscriptionId.trim().toLowerCase() : '';
            if (!GUID.test(subscriptionId)) {
                throw new InvalidCloudAccountError('subscriptionId must be a GUID, like 00000000-0000-0000-0000-000000000000');
            }
            const azureTenantId = typeof input.azureTenantId === 'string' ? input.azureTenantId.trim().toLowerCase() : '';
            if (!GUID.test(azureTenantId)) {
                throw new InvalidCloudAccountError('azureTenantId must be the GUID of the Entra tenant the subscription belongs to');
            }
            // A tenant id typed into the subscription box is the likeliest slip, and
            // both are GUIDs. They are never equal for a real subscription.
            if (subscriptionId === azureTenantId) {
                throw new InvalidCloudAccountError('subscriptionId and azureTenantId are the same GUID; one of them is the wrong value');
            }
            // No default. Subscription scope in a shared subscription hands Qrvey
            // every log in it, so which one applies is the customer's statement.
            if (!isDelegationScope(input.delegationScope)) {
                throw new InvalidCloudAccountError(`delegationScope must be one of: ${DELEGATION_SCOPES.join(', ')}`);
            }
            return {
                cloud: 'azure',
                subscriptionId,
                azureTenantId,
                delegationScope: input.delegationScope,
                label,
            };
        }
    }
};
/**
 * The ARN of the role we assume in a customer's AWS account.
 *
 * Only reachable from the AWS branch, which is the whole point of the
 * discriminated union: there is no way to ask for the ARN of a non-AWS account,
 * so the prototype's bug cannot be written here.
 */
export const customerRoleArn = (account, roleName) => {
    if (!AWS_ACCOUNT_ID.test(account.awsAccountId)) {
        throw new InvalidCloudAccountError(`not an AWS account id: ${account.awsAccountId}`);
    }
    return `arn:aws:iam::${account.awsAccountId}:role/${roleName}`;
};
/**
 * The role the control plane assumes in a customer's account.
 *
 * The *diagnostics* role, not a deploy role. The bootstrap template also creates
 * `QrveyDeployExecRole`, which is broad and assumable only by the in-account
 * Terraform runner — the control plane is deliberately not a trusted principal
 * of it, so a compromise here cannot become write access to a customer's
 * infrastructure. Naming the deploy role here would be a standing invitation to
 * try to assume it.
 */
export { DIAGNOSTICS_ROLE_NAME as CONTROL_PLANE_ROLE_NAME } from '../bootstrap/template.js';
//# sourceMappingURL=cloud-account.js.map